<template>
  <div class="inputs-view card">
      <div class="card-header">
        <div class="row w-100 g-2 g-sm-0 align-items-center">
          <div class="col-sm">
            <h3 class="mb-0">Strategy Brief</h3>
          </div>
          <div class="col-sm-auto d-flex justify-content-end">
            <button v-if="canEdit('playbooks')" class="btn btn-secondary" @click="showSmartImportModal" :disabled="isGenerating()">
              <img src="/img/icons/avid-icons/import-edna.svg" class="icon" alt=""> Smart Import
            </button>
            <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
              <button class="btn btn-secondary" :disabled="true">
                <img src="/img/icons/avid-icons/import-edna.svg" class="icon" alt=""> Smart Import
              </button>
            </div>
          </div>
        </div>
      </div>

    <div class="card-body"> 
      <div class="row">
        <div class="col-lg-6 col-12 mb-4" v-for="(prompt, key) in local_prompts" :key="key">
          <div class="content-card" :class="{ 'smart_scanned_card': if_generated[key], 'is-generating': isGenerating(key) }" style="border: none; box-shadow: none;">
              <div class="content-card-header" style="min-height: 72px;">
                <div class="row w-100 g-2 g-sm-0 align-items-center">
                  <div class="col-sm">
                    <div class="d-flex align-items-center gap-2">
                      <h4 class="mb-0">{{ getPromptTitle(key) }}</h4>
                      <i v-if="getPromptQuestion(key)" 
                         class="fa fa-info-circle text-muted" 
                         style="cursor: help; font-size: 16px;"
                         :title="getPromptQuestion(key)"
                         data-bs-toggle="tooltip"
                         data-bs-placement="top"></i>
                    </div>
                  </div>
                  <div class="col-sm-auto d-flex justify-content-end">
                    <button v-if="!if_generated[key] && !isGenerating(key) && canEdit('playbooks')" class="btn btn-primary" @click="regeneratePrompt(key)">
                      <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> <span v-if="prompt">Refine</span><span v-else>Generate</span>
                    </button>
                    <div v-else-if="!if_generated[key] && !isGenerating(key) && !canEdit('playbooks')" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                      <button class="btn btn-primary" :disabled="true">
                        <img src="/img/icons/avid-icons/generate-edna.svg" class="icon" alt=""> <span v-if="prompt">Refine</span><span v-else>Generate</span>
                      </button>
                    </div>
                    <div v-else-if="if_generated[key] && original_prompts[key]" class="d-flex gap-2 justify-content-end">
                        <button class="btn btn-secondary" @click="revertPrompt(key)">Revert</button>
                    </div>
                  </div>
                </div>
            </div>

              <div class="content-card-body">
              <div v-if="editing_prompt !== key || editing_mode !== 'content'">
                <p class="clickable-content" @click="startEditingContent(key, prompt)" :ref="`displayContent-${key}`">
                  <span v-if="prompt" v-html="htmlify(prompt)"></span>
                  <em v-else class="text-muted">{{ getPromptQuestion(key)}}</em>
                </p>
              </div>
              <div v-else>
                <textarea 
                  v-model="edit_answer" 
                  @blur="savePrompt(key)" 
                  @keyup.escape="cancelEditing"
                  class="form-control textarea-full-height p-0"
                  :ref="`answerInput-${key}`"
                  :style="getTextareaStyle(key)"
                  style="border-radius: 0;"
                ></textarea>
              </div>
              <div class="citations-container mx-0" v-if="getCitations(key).length > 0">
                <img src="/img/icons/avid-icons/edna.svg" class="icon me-1" alt=""></img>
                This strategy was developed using the following sources:
                <template v-for="(citation, idx) in getCitations(key)">
                  <template v-if="idx < getCitations(key).length - 2">
                    <a :key="citation.id" :href="getCitationLink(citation)" target="_blank">{{ citation.name }}</a><span>, </span>
                  </template>
                  <template v-else-if="idx == getCitations(key).length - 2">
                    <a :key="citation.id" :href="getCitationLink(citation)" target="_blank">{{ citation.name }}</a><span> and </span>
                  </template>
                  <template v-else>
                    <a :key="citation.id" :href="getCitationLink(citation)" target="_blank">{{ citation.name }}</a>
                  </template>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-6 col-12 mb-4">
          <div class="content-card" :class="{ 'editing': is_adding_custom_input }" style="cursor: pointer;">
            <div class="content-card-header d-flex justify-content-between align-items-center" style="min-height: 72px;">
              <div v-if="!is_adding_custom_input || editing_mode !== 'title'" class="flex-grow-1" @click="startAddingCustomInput">
                <h4 class="mb-0">{{ custom_input_title || 'Custom Input' }}</h4>
              </div>
              <div v-else class="flex-grow-1">
                <input 
                  v-model="custom_input_title" 
                  @blur="saveCustomInput" 
                  @keyup.enter="saveCustomInput"
                  class="form-control"
                  placeholder="Enter title"
                  ref="customTitleInput" aria-label="Custom Input Title">
              </div>
            </div>
            <div class="content-card-body">
              <div v-if="!is_adding_custom_input || editing_mode !== 'content'" @click="startEditingCustomContent">
                <p class="clickable-content">
                  <span v-html="htmlify(custom_input_answer)" v-if="custom_input_answer"></span>
                  <em class="text-muted" v-if="!custom_input_answer">If there is something specific you want to include for touchpoint generation, you can do so here.</em>
                </p>
              </div>
              <div v-else>
                <textarea 
                  v-model="custom_input_answer" 
                  @blur="saveCustomInput" 
                  class="form-control textarea-full-height"
                  placeholder="Enter content"
                  ref="customAnswerInput"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-6 col-12 mb-4" v-if="hasImageRequirements">
          <div class="content-card" style="border: none; box-shadow: none;">
            <div class="content-card-header d-flex justify-content-between align-items-center" style="min-height: 72px;">
              <div class="d-flex align-items-center gap-2">
                <h4 class="mb-0">Playbook Base Images <em>(Optional)</em></h4>
                <i class="fa fa-info-circle text-muted" 
                   style="cursor: help; font-size: 16px;"
                   title="These images serve as the starting point for image generation as it relates to the style, subjects, and overall look and feel."
                   data-bs-toggle="tooltip"
                   data-bs-placement="top"></i>
              </div>
            </div>
            <div class="content-card-body">
              <div class="image-array-section">
                <div class="d-flex flex-wrap gap-2 align-items-start">
                  <!-- Display images as thumbnails -->
                  <div v-for="(image, image_index) in getValidImages()" :key="image_index" class="position-relative image-thumbnail-wrapper" style="max-width: 200px;">
                    <img 
                      :src="getImageUrl(image)" 
                      :alt="image.name || 'Uploaded image'" 
                      class="image-thumbnail"
                      style="height: 100px; width: auto; max-width: 100%; object-fit: contain; border-radius: 20px; cursor: pointer;"
                      @click.prevent="openImagePreview(image_index)"
                    />
                    <div class="position-absolute top-0 end-0 d-flex gap-1 m-1">
                      <button 
                        v-if="canEdit('playbooks')"
                        type="button" 
                        class="btn btn-icon-only-sm btn-secondary p-1 mt-1" 
                        data-bs-toggle="tooltip" title="Remove"
                        @click.stop.prevent="removeImage(image_index)"
                        :disabled="isGenerating()"
                        style="display: flex; align-items: center; justify-content: center;"
                      >
                        <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                      </button>
                    </div>
                  </div>
                  
                  <!-- Drag and drop placeholder to add new image -->
                  <div 
                    v-if="canEdit('playbooks')"
                    class="image-dropzone-placeholder"
                    @click.prevent="openImageUploadModal()"
                    @dragover.prevent="handleDragOver"
                    @drop.prevent="handleDrop"
                    style="width: 100px; height: 100px; border-radius: 20px; border: 2px dashed #ccc; display: flex; align-items: center; justify-content: center; cursor: pointer; background: #f8f9fa;"
                  >
                    <div class="text-center">
                      <img src="/img/icons/avid-icons/plus.svg" class="icon" style="width: 24px; height: 24px;" alt="">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="row">
        <div class="col-12 d-flex justify-content-end">
          <button v-if="canEdit('playbooks')" class="btn btn-primary" @click="saveStrategy"><img src="/img/icons/avid-icons/save.svg" class="icon" alt=""> Save</button>
          <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
            <button class="btn btn-primary" :disabled="true"><img src="/img/icons/avid-icons/save.svg" class="icon" alt=""> Save</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Smart Import Modal -->
    <div class="modal fade" id="smartImportModal" tabindex="-1" aria-labelledby="smartImportModalLabel" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header pb-0">
            <h2 class="modal-title fw-bold text-center w-100 mb-0">Smart Import</h2>
            <button type="button" class="btn btn-white float-end close-modal" @click="closeSmartImportModal" aria-label="Close" data-bs-toggle="tooltip" title="Close">
              <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
            </button>
          </div>

          <!-- Modal Body -->
          <div class="modal-body px-4 pb-4">
            <!-- Description -->
            <p class="text-muted mb-4">
              Smart Import lets you upload documents directly into Playbooks. Avid will scan the file, extracts key details, and automatically fills in the strategy questions for your new Play. It's the fastest way to reuse what already worked and jumpstart your next campaign.
            </p>

            <!-- URL Input -->
            <div class="mb-4">
              <input 
                type="url" 
                id="document_url"
                v-model="smart_import_data.document_url"
                class="form-control" 
                :class="{ 'is-invalid': smart_import_data.error_message && smart_import_data.document_url }"
                placeholder="Add URL"
                @input="clearErrorMessage" aria-label="Document Url">
            </div>

            <!-- Divider with OR text -->
            <div class="d-flex align-items-center mb-4">
              <hr class="flex-grow-1">
              <span class="mx-3 text-muted">OR</span>
              <hr class="flex-grow-1">
            </div>

            <!-- File Upload Area -->
            <div class="">
              <FileDropzone 
                id="smartImportDropzone"
                upload-method="proxy"
                :clientUrl="client?.url"
                :on-file-uploaded="handleFileUploaded"
                @uploadProgress="handleUploadProgress"
                @uploadError="({ message }) => smart_import_data.error_message = message"
              />
            </div>

            <!-- Upload Progress Display -->
            <div v-if="Object.keys(smart_import_data.upload_progress).length > 0" class="upload-progress mt-3">
              <div v-for="(progress, fileName) in smart_import_data.upload_progress" :key="fileName" class="mb-2">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <small class="text-muted">{{ fileName }}</small>
                  <small class="text-muted">{{ progress }}%</small>
                </div>
                <div class="progress" style="height: 4px;">
                  <div 
                    class="progress-bar" 
                    role="progressbar" 
                    :style="{ width: progress + '%' }"
                    :aria-valuenow="progress" 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Uploaded File Display -->
            <div v-if="smart_import_data.uploaded_file" class="uploaded-file mt-3">
              <div class="d-flex align-items-center justify-content-between bg-light p-3">
                <div class="d-flex align-items-center">
                  <i class="fa fa-file-text me-3 text-primary"></i>
                  <span class="text-break">{{ smart_import_data.uploaded_file.name }}</span>
                  <small class="text-muted mx-2 text-nowrap">({{ formatFileSize(smart_import_data.uploaded_file.size_bytes || 0) }})</small>
                </div>
                <button type="button" class="btn btn-primary btn-icon-only-sm" @click="clearselected_file">
                  <img src="/img/icons/avid-icons/trash.svg" class="icon" alt=""></img>
                </button>
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="smart_import_data.error_message" class="alert alert-danger mt-3" role="alert">
              {{ smart_import_data.error_message }}
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer">
            <div class="w-100 d-flex justify-content-end">
              <button 
                type="button" 
                class="btn"
                :class="canScan ? 'btn-primary' : 'btn-secondary'"
                :disabled="!canScan"
                @click="scanDocument"
              >
                {{ smart_import_data.is_scanning ? 'Processing...' : 'Scan' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Regenerate Feedback Modal -->
    <div class="modal fade" id="regenerateFeedbackModal" tabindex="-1" aria-labelledby="regenerateFeedbackModalLabel" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header pb-0">
            <h3 class="modal-title fw-bold text-center w-100 mb-0">Refine Strategy</h3>
            <button type="button" class="btn btn-white float-end close-modal" @click="closeRegenerateFeedbackModal" aria-label="Close" data-bs-toggle="tooltip" title="Close">
              <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
            </button>
          </div>

          <!-- Modal Body -->
          <div class="modal-body px-4 pb-4">
              <label for="regenerate_feedback" class="form-label">Describe the changes you'd like:</label>
              <textarea 
                id="regenerate_feedback"
                v-model="regenerate_feedback_data.feedback_text"
                class="form-control" 
                rows="4"
                placeholder="e.g., Make it more focused on this segment, use this webpage as a reference, talk about this initiative..."
                :class="{ 'is-invalid': regenerate_feedback_data.error_message }"
              ></textarea>
              <div v-if="regenerate_feedback_data.error_message" class="invalid-feedback">
                {{ regenerate_feedback_data.error_message }}
              </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer">
            <div class="w-100 d-flex justify-content-end">
              <button 
                type="button" 
                class="btn btn-secondary me-2"
                @click="closeRegenerateFeedbackModal"
              >
                Cancel
              </button>
              <button 
                type="button" 
                class="btn btn-primary"
                :disabled="!regenerate_feedback_data.feedback_text.trim()"
                @click="submitRegenerateFeedback"
              >
                <img class="icon me-2" src="/img/icons/avid-icons/edna.svg" style="width: 20px; height: 20px;" alt="">
                Refine
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Add Base Images Modal (split: library OR upload) -->
    <div class="modal fade" id="imageUploadModal" tabindex="-1" aria-labelledby="imageUploadModalLabel" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header border-bottom-0 pb-0">
            <h5 class="modal-title" id="imageUploadModalLabel">Add base images</h5>
            <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close">
              <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
            </button>
          </div>
          <div class="modal-body px-4 pb-4">
            <p class="text-muted mb-4">
              Add images from your library or upload new ones. They will be used as style references for touchpoint generation.
            </p>

            <!-- From library -->
            <div class="mb-4">
              <label class="form-label">From library</label>
              <div v-if="image_modal_library.loading" class="text-center py-3">
                <div class="spinner-border spinner-border-sm" role="status"></div>
                <span class="ms-2 text-muted">Loading images...</span>
              </div>
              <div v-else class="d-flex align-items-center gap-2">
                <button
                  type="button"
                  class="btn btn-white btn-icon-only-sm flex-shrink-0"
                  :disabled="image_modal_library.slideIndex <= 0"
                  @click="imageModalLibraryPrev"
                  aria-label="Previous"
                  data-bs-toggle="tooltip"
                  title="Previous"
                >
                  <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                </button>
                <div
                  ref="imageModalCarouselWrapper"
                  class="image-modal-carousel-wrapper flex-grow-1 overflow-hidden min-width-0"
                >
                  <div
                    class="image-modal-carousel-track d-flex gap-2"
                    :style="imageModalCarouselTrackStyle"
                  >
                    <div
                      v-for="image in image_modal_library_display_images"
                      :key="image.id"
                      class="image-modal-library-card flex-shrink-0 border rounded overflow-hidden"
                      :class="{ 'image-modal-library-card-selected': isLibraryImageSelected(image.id) }"
                      @click="toggleLibraryImageSelection(image)"
                    >
                      <img
                        :src="getLibraryImageUrl(image)"
                        :alt="image.name"
                        class="image-modal-library-thumb"
                      >
                      <div v-if="isLibraryImageSelected(image.id)" class="image-modal-library-overlay"></div>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-white btn-icon-only-sm flex-shrink-0"
                  :disabled="!canImageModalLibraryNext"
                  @click="imageModalLibraryNext"
                  aria-label="Next"
                  data-bs-toggle="tooltip"
                  title="Next"
                >
                  <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                </button>
              </div>
              <small v-if="!image_modal_library.loading && image_modal_library_display_images.length === 0" class="text-muted">No library images yet.</small>
            </div>

            <!-- OR divider -->
            <div class="d-flex align-items-center mb-3">
              <hr class="flex-grow-1">
              <span class="mx-3 text-muted">OR</span>
              <hr class="flex-grow-1">
            </div>

            <!-- Upload new images -->
            <div>
              <label class="form-label">Upload new images</label>
              
              <ImageDropzone
                :key="'image-modal-dropzone-' + image_modal_upload.dropzoneKey"
                v-model="image_modal_upload.currentUrl"
                class="image-dropzone-wrapper"
                id="modal-image-dropzone"
                :clientUrl="client?.url"
                :max-files="20"
                :min-height="180"
                :disabled="isGenerating()"
                :if_thumbnail="false"
                :hide-delete-button="true"
                @update:modelValue="handleModalUploadImageUpdate"
                @file-uploaded="handleModalUploadImageUpdate"
                @upload-progress="handleModalUploadProgress"
              />
              <div v-if="image_modal_upload.uploadProgress" class="alert alert-light border d-flex align-items-center gap-2 mt-2">
                <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                <span class="small text-muted">
                  Uploading {{ image_modal_upload.uploadProgress.fileName }}
                  <span v-if="image_modal_upload.uploadProgress.totalFiles > 1">
                    ({{ image_modal_upload.uploadProgress.fileIndex }} of {{ image_modal_upload.uploadProgress.totalFiles }})
                  </span>
                  — {{ image_modal_upload.uploadProgress.percent }}%
                </span>
                <div class="progress flex-grow-1" style="height: 6px;">
                  <div
                    class="progress-bar"
                    role="progressbar"
                    :style="{ width: image_modal_upload.uploadProgress.percent + '%' }"
                  ></div>
                </div>
              </div>
              <div v-if="image_modal_upload.pendingUrls.length > 0" class="d-flex flex-wrap gap-2 mt-2">
                <div
                  v-for="(item, idx) in image_modal_upload.pendingUrls"
                  :key="item.key"
                  class="position-relative image-modal-upload-thumb"
                >
                  <img :src="normalizeImageUrl(item.url)" alt="Upload" class="rounded">
                  <button
                    type="button"
                    class="btn btn-sm btn-white position-absolute top-0 end-0 m-1 shadow-sm"
                    @click="removePendingUploadUrl(idx)"
                    aria-label="Remove"
                  >
                    <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!canAddImagesFromModal || isGenerating()"
              @click="confirmAddImagesFromModal"
            >
              {{ addImagesModalPrimaryLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Preview Modal -->
    <div class="modal fade" id="imagePreviewModal" tabindex="-1" aria-labelledby="imagePreviewModalLabel" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header border-bottom-0 pb-0">
            <h5 class="modal-title" id="imagePreviewModalLabel">
              {{ previewImageData.name || 'Preview Image' }}
            </h5>
            <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close" @click="closeImagePreviewModal">
              <img class="icon" src="/img/icons/avid-icons/close.svg" alt="">
            </button>
          </div>
          <div class="modal-body px-4 pb-4">
            <div v-if="previewImageData.url" class="d-flex justify-content-center align-items-center" style="min-height: 400px;">
              <img 
                :src="previewImageData.url" 
                alt="Preview image" 
                class="img-fluid"
                style="max-width: 100%; max-height: 70vh; object-fit: contain; border-radius: 20px;"
              />
            </div>
            <div v-if="previewImageData.image_classification" class="mt-3 text-center">
              Classification: <em class="text-muted">
                {{ formatClassification(previewImageData.image_classification) }}
              </em>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { usePlaybookStore } from '../../stores/playbook'
import { useClientStore } from '../../stores/client'
import { useNotifications } from '../../composables/useNotifications'
import Swal from 'sweetalert2'
import permissionsMixin from '../../mixins/permissions'
import FileDropzone from '../components/FileDropzone.vue'
import ImageDropzone from '../components/ImageDropzone.vue'
import statsigService from '../../services/statsig'

export default {
  name: 'PlayStrategyView',
  mixins: [permissionsMixin],
  components: {
    FileDropzone,
    ImageDropzone
  },
  setup() {
    const playbookStore = usePlaybookStore()
    const clientStore = useClientStore()
    const { showNotification } = useNotifications()
    return {
      playbookStore,
      clientStore,
      showNotification
    }
  },
  data() {
    return {
      editing_prompt: null,
      editing_mode: null, // 'title' or 'content'
      edit_title: '',
      edit_answer: '',
      is_adding_custom_input: false,
      custom_input_title: '',
      custom_input_answer: '',
      smart_import_data: {
        document_url: '',
        selected_file: null,
        uploaded_file: null,
        error_message: '',
        is_scanning: false,
        upload_progress: {}
      },
      regenerate_feedback_data: {
        prompt_key: '',
        feedback_text: '',
        error_message: ''
      },
      local_prompts: {},
      if_generated: {}, // Object to track which prompts have been regenerated (key: prompt_key, value: true)
      generating_prompts: [], // Array to track which prompts are generating
      original_prompts: {}, // Store original content before regeneration
      initial_prompts_snapshot: {},
      edit_area_heights: {},
      playbookImages: [], // Selected images for the playbook
      initial_images_snapshot: [], // Store initial images for change detection
      pending_images: [], // Images pending save (includes new uploads)
      pending_image_deletes: [], // Image IDs to delete on save
      upload_image_data: {
        image_url: '',
        image_classification: '',
        error_message: '',
        resetKey: 0
      },
      image_modal_library: {
        loading: false,
        images: [],
        slideIndex: 0,
        total: 0,
        selectedIds: [],
        carouselGap: 8,
        visibleCount: 6,
        wrapperWidth: 0
      },
      image_modal_upload: {
        pendingUrls: [],
        currentUrl: '',
        dropzoneKey: 0,
        uploadProgress: null
      },
      previewImageData: {
        url: '',
        name: '',
        image_classification: ''
      },
      previewImageIndex: null
    }
  },
    beforeMount() {
    if (!this.playbook.prompts || Object.keys(this.playbook.prompts).length === 0) {
      let inputs = {};
      const typePrompts = this.playbook.playbook_type?.prompts ?? [];
      for (const prompt of typePrompts) {
        const placeholder = prompt.title.toLowerCase()
          .replace(/[^a-z\s]/g, '')
          .replace(/ /g, '_');
        inputs[placeholder] = '';
      }
      const overrides = this.playbook?.playbook_suggestion?.result?.strategy_prompt_overrides;
      if (typeof overrides === 'object' && Object.keys(inputs).length === 0) {
        for (const rawKey of Object.keys(overrides)) {
          const key = this.sanitizePromptKey(rawKey);
          if (key) inputs[key] = '';
        }
      }
      this.playbook.prompts = inputs;
    }
    
    // Load playbook images if they exist
    if (this.playbook?.images) {
      this.playbookImages = this.playbook.images;
      this.pending_images = JSON.parse(JSON.stringify(this.playbook.images));
      this.initial_images_snapshot = JSON.parse(JSON.stringify(this.playbook.images));
    } else {
      this.playbookImages = [];
      this.pending_images = [];
      this.initial_images_snapshot = [];
    }
    //If they have a custom input, I need to move it into the custom fields and out of the prompts
    if(this.hasCustomInput()) {
      //Loop through the prompts to find the one that isn't in the playbook_type prompts
      for(let key in this.playbook.prompts) {
        if(this.isCustomInput(key)) {
          this.custom_input_title = key;
          break;
        }
      }
      this.custom_input_answer = this.playbook.prompts[this.custom_input_title];
      this.custom_input_title = this.custom_input_title.replace(/_/g, ' ');
      this.custom_input_title = this.custom_input_title.charAt(0).toUpperCase() + this.custom_input_title.slice(1);
    }
    this.local_prompts = this.setupLocalPrompts();
    this.initial_prompts_snapshot = JSON.parse(JSON.stringify(this.playbook.prompts));
  },
  mounted() {
    // Initialize Bootstrap tooltips
    this.$nextTick(() => {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.map(function (tooltipTriggerEl) {
        return new bootstrap.Tooltip(tooltipTriggerEl);
      });
    });

    // Subscribe to Pusher events for prompt regeneration
    this.subscribeToEvents();
  },
  beforeUnmount() {
    if (this._carouselResizeObserver) {
      this._carouselResizeObserver.disconnect();
    }
    if (window.Echo && this.client && this.client.id) {
      window.Echo.private('client.' + this.client.id)
        .stopListening('PlaybookPromptsGenerated')
        .stopListening('ClientImageProcessed');
    }
  },
  computed: {
    client() {
      return this.clientStore.getClient;
    },
    playbook() {
      return this.playbookStore.getSelectedPlaybook;
    },
    canScan() {
      const hasUrl = this.smart_import_data.document_url && this.smart_import_data.document_url.trim().length > 0;
      const hasFile = this.smart_import_data.uploaded_file !== null;
      const notScanning = !this.smart_import_data.is_scanning;
      
      const canScan = (hasUrl || hasFile) && notScanning;
      
      return canScan;
    },
    
    isValidUrl() {
      if (!this.smart_import_data.document_url || this.smart_import_data.document_url.trim().length === 0) {
        return true; // Empty URL is valid (user might upload file instead)
      }
      
      try {
        new URL(this.smart_import_data.document_url);
        return true;
      } catch {
        return false;
      }
    },
    hasImageRequirements() {
      // Hide image area if client has image_preference of "none"
      if (this.client?.image_preference === 'none') {
        return false;
      }
      
      // Show image area if playbook has images (e.g., from Google Doc import)
      const hasImages = this.getValidImages().length > 0;
      if (hasImages) {
        return true;
      }

      // Check if playbook type has asset types with image requirements
      if (!this.playbook?.playbook_type?.asset_types) {
        return false;
      }
      
      return this.playbook.playbook_type.asset_types.some(assetType => {
        // Check if pivot has image_style_ids
        if (assetType.pivot?.image_style_ids && assetType.pivot.image_style_ids.length > 0) {
          return true;
        }
        
        // Check if asset type has image inputs
        if (assetType.inputs) {
          return assetType.inputs.some(input => input.type === 'image');
        }
        
        return false;
      });
    },
    canAddImagesFromModal() {
      const hasLibrarySelection = this.image_modal_library.selectedIds.length > 0;
      const hasUploads = this.image_modal_upload.pendingUrls.length > 0;
      if (hasLibrarySelection && hasUploads) {
        return false;
      }
      return hasLibrarySelection || hasUploads;
    },
    addImagesModalPrimaryLabel() {
      const libCount = this.image_modal_library.selectedIds.length;
      const uploadCount = this.image_modal_upload.pendingUrls.length;
      if (libCount > 0) {
        return `Add selected (${libCount})`;
      }
      if (uploadCount > 0) {
        return `Add ${uploadCount} image${uploadCount !== 1 ? 's' : ''}`;
      }
      return 'Add images';
    },
    image_modal_library_carousel_transform() {
      const idx = this.image_modal_library.slideIndex;
      const gap = this.image_modal_library.carouselGap;
      // One page = wrapper width. Add inter-page gap to keep alignment clean.
      return `translateX(calc(-${idx * 100}% - ${idx * gap}px))`;
    },
    imageModalCarouselTrackStyle() {
      return {
        transform: this.image_modal_library_carousel_transform,
        transition: 'transform 0.35s ease-out',
        willChange: 'transform'
      };
    },
    playbookImageIds() {
      return this.getValidImages()
        .filter(img => img && img.id && !String(img.id).startsWith('temp_'))
        .map(img => img.id);
    },
    image_modal_library_display_images() {
      const ids = this.playbookImageIds;
      return this.image_modal_library.images.filter(img => !ids.includes(img.id));
    },
    canImageModalLibraryNext() {
      const lib = this.image_modal_library;
      const displayImages = this.image_modal_library_display_images;
      const nextWindowStart = (lib.slideIndex + 1) * lib.visibleCount;
      if (nextWindowStart < displayImages.length) {
        return true;
      }
      if (lib.images.length < lib.total) {
        return true;
      }
      return false;
    },
    /** Map of sanitized key -> { title, question } from playbook suggestion strategy_prompt_overrides (fallback when key not in type prompts). */
    promptDisplayFallback() {
      const overrides = this.playbook?.playbook_suggestion?.result?.strategy_prompt_overrides;
      if (!overrides || typeof overrides !== 'object') return {};
      const map = {};
      for (const [rawKey, question] of Object.entries(overrides)) {
        const key = this.sanitizePromptKey(rawKey);
        if (key && typeof question === 'string') {
          map[key] = { title: this.keyToTitle(key), question };
        }
      }
      return map;
    }
  },
  methods: {
    sanitizePromptKey(key) {
      if (key == null) return '';
      let s = String(key).replace(/_/g, ' ').toLowerCase();
      s = s.replace(/[^a-z\s]/g, '');
      s = s.trim().replace(/\s+/g, '_');
      return s;
    },
    keyToTitle(key) {
      if (!key || typeof key !== 'string') return '';
      return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    },
    resetUnsavedChanges() {
      // Revert prompts to initial snapshot
      this.playbook.prompts = JSON.parse(JSON.stringify(this.initial_prompts_snapshot));
      this.local_prompts = this.setupLocalPrompts();
      // reset custom input fields
      this.custom_input_title = '';
      this.custom_input_answer = '';
      this.is_adding_custom_input = false;
      // reset editing state
      this.editing_prompt = null;
      this.editing_mode = null;
      this.edit_title = '';
      this.edit_answer = '';
      // reset generation flags
      this.if_generated = {};
      this.original_prompts = {};
      // reset images to initial snapshot
      this.pending_images = JSON.parse(JSON.stringify(this.initial_images_snapshot));
      this.pending_image_deletes = [];
    },
    setupLocalPrompts() {
      let prompts = JSON.parse(JSON.stringify(this.playbook.prompts));
      if(this.hasCustomInput()) {
        let placeholder;
        for(let key in this.playbook.prompts) {
          if(this.isCustomInput(key)) {
            placeholder = key;
            break;
          }
        }
        delete prompts[placeholder];
      }
      //Remove the citation keys too
      for(let key in prompts) {
        if(key.endsWith('_citations')) {
          delete prompts[key];
        }
      }
      return prompts;
    },
    getCitations(key) {
      let prompts = this.playbook.prompts;
      for (let loop_key in prompts) {
        if(loop_key  === key + "_citations") {
          let citations = prompts[loop_key];
          //If citations is an object, make it into an array
          if(typeof citations === 'object') {
            citations = Object.values(citations);
          }
          //Remove the 'Brand Information' citations
          citations = citations.filter(citation => citation.name !=='Brand Information');
          return citations;
        }
      }
      return [];
    },
    isCustomInput(key) {
      if (key.indexOf('_citations') !== -1) {
        return false;
      }
      const promptsArr = this.playbook.playbook_type?.prompts ?? [];
      for (const prompt of promptsArr) {
        const title_placeholder = prompt.title.toLowerCase()
          .replace(/[^a-z\s]/g, '')
          .replace(/ /g, '_');
        if (title_placeholder === key) {
          return false;
        }
      }
      if (this.promptDisplayFallback[key]) {
        return false;
      }
      return true;
    },
    hasCustomInput() {
      //Look through the playbook.prompts and see if any of the keys are custom inputs
      for(let key in this.playbook.prompts) {
        if(this.isCustomInput(key)) {
          return true;
        }
      }
      return false;
    },
    htmlify(value) {
      if (value == null || typeof value !== "string") return null;
      return value.replace(/\n/g, "<br />");
    },
    getCitationLink(citation) {
      if(citation.source_url) {
        return citation.source_url;
      }
      else if (this.client && this.client.url) {
        return '/internal/' + this.client.url + '/redirect/' + citation.id;
      }
      // Return a fallback hash link if client is not available during initial render
      return '#';
    },
    getPromptTitle(placeholder) {
      const promptsArr = this.playbook.playbook_type?.prompts ?? [];
      for (const prompt of promptsArr) {
        const title_placeholder = prompt.title.toLowerCase()
          .replace(/[^a-z\s]/g, '')
          .replace(/ /g, '_');
        if (title_placeholder === placeholder) {
          return prompt.title;
        }
      }
      const fallback = this.promptDisplayFallback[placeholder];
      if (fallback) return fallback.title;
      return this.keyToTitle(placeholder) || placeholder;
    },
    getPromptQuestion(placeholder) {
      const promptsArr = this.playbook.playbook_type?.prompts ?? [];
      for (const prompt of promptsArr) {
        const title_placeholder = prompt.title.toLowerCase()
          .replace(/[^a-z\s]/g, '')
          .replace(/ /g, '_');
        if (title_placeholder === placeholder) {
          return prompt.question || '';
        }
      }
      const fallback = this.promptDisplayFallback[placeholder];
      if (fallback) return fallback.question || '';
      return '';
    },
    startEditingTitle(index, prompt) {
      this.editing_prompt = index;
      this.editing_mode = 'title';
      this.edit_title = prompt.title || '';
      this.edit_answer = prompt.answer || '';
      
      // Focus on the input after the DOM updates
      this.$nextTick(() => {
        const titleInput = this.$refs[`titleInput-${index}`];
        if (titleInput && titleInput[0]) {
          titleInput[0].focus();
        }
      });
    },
    
    startEditingContent(key, prompt) {
      // Measure the currently rendered content height so textarea matches it
      const displayEl = this.$refs[`displayContent-${key}`];
      const node = Array.isArray(displayEl) ? displayEl[0] : displayEl;
      if (node && node.offsetHeight) {
        this.edit_area_heights[key] = node.offsetHeight;
      }

      this.editing_prompt = key;
      this.editing_mode = 'content';
      this.edit_answer = prompt;
      
      // Focus on the textarea after the DOM updates
      this.$nextTick(() => {
        const answerInput = this.$refs[`answerInput-${key}`];
        const el = Array.isArray(answerInput) ? answerInput[0] : answerInput;
        if (el) {
          el.focus();
        }
      });
    },
    
    cancelEditing() {
      this.editing_prompt = null;
      this.editing_mode = null;
      this.edit_title = '';
      this.edit_answer = '';
    },
    
    savePrompt(key) {
      if (this.editing_prompt !== key) return;
      
      // Ensure prompts object exists
      if (!this.playbook.prompts) {
        this.playbook.prompts = {};
      }
      
      this.playbook.prompts[key] = this.edit_answer;
      this.local_prompts[key] = this.edit_answer;
      this.cancelEditing();
      // Clear cached height for this key
      if (this.edit_area_heights[key]) {
        delete this.edit_area_heights[key];
      }
      
      // Emit event to parent that strategy has changed
      this.$emit('strategy-changed');
    },

    saveStrategy() {
      const onlyImagesChanged = this.hasOnlyImagesChanged();
      
      let htmlContent = '';
      if (onlyImagesChanged) {
        htmlContent = `
          <div>
            <p>Are you sure you want to save these image changes?</p>
            <div class="form-check mt-2 mx-auto d-flex flex-column align-items-center" style="max-width: 370px; text-align: left;">
              <div class="w-100 text-start">
                <input class="form-check-input" type="checkbox" id="regenerateImages" aria-label="Checkbox">
                <label class="form-check-label" for="regenerateImages">
                  Also regenerate all touchpoint images based upon these new images
                </label>
              </div>
            </div>
          </div>
        `;
      } else {
        htmlContent = `
          <div>
            <p>Are you sure you want to save these the changes you've made to the questions above?</p>
            <div class="form-check mt-2 mx-auto d-flex flex-column align-items-center" style="max-width: 370px; text-align: left;">
              <div class="w-100 text-start">
                <input class="form-check-input" type="checkbox" id="regenerateTouchpoints" aria-label="Checkbox">
                <label class="form-check-label" for="regenerateTouchpoints">
                  Also regenerate all touchpoints based upon these new answers
                </label>
              </div>
            </div>
          </div>
        `;
      }
      
      // Show SweetAlert with checkbox asking about regenerating
      Swal.fire({
        title: 'Save Changes?',
        html: htmlContent,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: 'Cancel',
        customClass: {
          popup: 'swal-warning'
        },
        preConfirm: () => {
          if (onlyImagesChanged) {
            const regenerateImages = document.getElementById('regenerateImages').checked;
            return { regenerateImages, regenerateTouchpoints: false };
          } else {
            const regenerateTouchpoints = document.getElementById('regenerateTouchpoints').checked;
            return { regenerateImages: false, regenerateTouchpoints };
          }
        }
      }).then((result) => {
        if (result.isConfirmed) {
          const { regenerateImages, regenerateTouchpoints } = result.value;
          this.updatePlaybook(regenerateTouchpoints, regenerateImages);
        }
      });
    },
    
    updatePlaybook(regenerateTouchpoints = false, regenerateImages = false) {

      //Make a copy of the prompts so we don't modify the original
      let original_prompts = JSON.parse(JSON.stringify(this.local_prompts));
      //If they have a custom input, I need to move it into the prompts and out of the custom fields
      if(this.custom_input_answer) {
        let custom_placeholder = this.custom_input_title.toLowerCase()
          .replace(/[^a-z\s]/g, '')
          .replace(/ /g, '_');
          original_prompts[custom_placeholder] = this.custom_input_answer;
      }
      //Add back the citation keys too - use current citations (which may include new ones from regeneration)
      for(let key in this.playbook.prompts) {
        if(key.endsWith('_citations')) {
          original_prompts[key] = this.playbook.prompts[key];
        }
      }

      // Prepare pending image uploads (images with is_pending flag)
      const pendingImageUploads = this.pending_images
        .filter(img => img.is_pending)
        .map(img => ({
          image_url: img.public_url,
          image_classification: img.image_classification
        }));

      // Create a payload with the updated playbook data
      const payload = {
        //Send the original playbook data so we don't lose any data
        ...this.playbook,
        prompts: original_prompts,
        regenerate_touchpoints: regenerateTouchpoints,
        regenerate_images: regenerateImages,
        pending_image_uploads: pendingImageUploads,
        pending_image_deletes: this.pending_image_deletes,
        image_ids: this.getValidImages()
          .filter(img => img.id && !img.id.toString().startsWith('temp_'))
          .map(img => img.id)
      };

      // Send the update request using axios (same as PlaybookOverview.vue)
      window.axios.put(`/api/playbooks/${this.client.url}/${this.playbook.url}`, payload)
        .then(response => {
          // Update the store with the new data
          this.playbookStore.setSelectedPlaybook(response.data.playbook);

          // Update images from response
          if (response.data.playbook?.images) {
            this.playbookImages = response.data.playbook.images;
            this.pending_images = JSON.parse(JSON.stringify(response.data.playbook.images));
            this.initial_images_snapshot = JSON.parse(JSON.stringify(response.data.playbook.images));
            this.pending_image_deletes = [];
          }

          // Clear all prompts from if_generated after successful save
          this.if_generated = {};

          // Emit event to parent that strategy has been saved
          this.$emit('strategy-saved');
          
          // Show success message
          let successMessage = '';
          if (regenerateImages) {
            successMessage = '<h4 class="mb-0">Your play has been updated and touchpoint images are regenerating!</h4>';
          } else if (regenerateTouchpoints) {
            successMessage = '<h4 class="mb-0">Your play has been updated and all touchpoints are regenerating!</h4>';
          } else {
            successMessage = '<h4 class="mb-0">Your play has been updated!</h4>';
          }
          
          // Extract text from HTML message
          const textMatch = successMessage.match(/<h4[^>]*>(.*?)<\/h4>/);
          const notificationText = textMatch ? textMatch[1] : 'Your play has been updated!';
          this.showNotification({
            icon: '/img/icons/nav-icons/Playbooks.svg',
            text: notificationText
          });
        })
        .catch(error => {
          console.error('Error updating playbook:', error);

          // Show error notification
          this.showNotification({
            icon: '/img/icons/avid-icons/danger.svg',
            text: 'Failed to save strategy'
          });
          
          throw error;
        });
    },

    showSmartImportModal() {
      const modalElement = document.getElementById('smartImportModal');
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    },

    closeSmartImportModal() {
      const modalElement = document.getElementById('smartImportModal');
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
      this.resetSmartImportForm();
    },

    resetSmartImportForm() {
      this.smart_import_data.document_url = '';
      this.smart_import_data.selected_file = null;
      this.smart_import_data.uploaded_file = null;
      this.smart_import_data.error_message = '';
      this.smart_import_data.is_scanning = false;
      this.smart_import_data.upload_progress = {};
    },

    clearErrorMessage() {
      if (this.smart_import_data.error_message) {
        this.smart_import_data.error_message = '';
      }
    },

    handleFileUploaded(fileData) {
      // File has been successfully uploaded - just store it, don't process yet
      this.smart_import_data.uploaded_file = fileData;
      this.smart_import_data.error_message = '';
      
      // Clean up progress tracking for completed upload
      if (this.smart_import_data.upload_progress[fileData.name]) {
        delete this.smart_import_data.upload_progress[fileData.name];
      }
      
      // console.log('File uploaded successfully:', fileData);
      // Don't call uploadFileToPlaybook here - wait for scan button
    },

    handleUploadProgress(progressData) {
      // Update progress for the file being uploaded
      this.smart_import_data.upload_progress[progressData.fileName] = progressData.progress;
    },

    clearselected_file() {
      //Call the delete endpoint for client documents
      window.axios.delete(`/api/document/${this.client.url}/${this.smart_import_data.uploaded_file.id}`);
      this.smart_import_data.uploaded_file = null;
      this.smart_import_data.error_message = '';
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    async scanDocument() {
      if (!this.canScan) return;

      // Clear any previous error messages
      this.smart_import_data.error_message = '';

      // Validate URL if provided
      if (this.smart_import_data.document_url && this.smart_import_data.document_url.trim().length > 0) {
        if (!this.isValidUrl) {
          this.smart_import_data.error_message = 'Please enter a valid URL';
          return;
        }
      }

      // Validate file if provided
      if (!this.smart_import_data.document_url && !this.smart_import_data.uploaded_file) {
        this.smart_import_data.error_message = 'Please provide a URL or upload a file';
        return;
      }

      // Set scanning state
      this.smart_import_data.is_scanning = true;

      try {
        if (this.smart_import_data.document_url) {
          // Handle URL upload
          await this.uploadUrlToPlaybook(this.smart_import_data.document_url);
        } else if (this.smart_import_data.uploaded_file) {
          // Handle file upload
          await this.uploadFileToPlaybook(this.smart_import_data.uploaded_file);
        }
      } catch (error) {
        console.error('Error during document scanning:', error);
        this.smart_import_data.error_message = error.response?.data?.message || error.message || 'Failed to process document. Please try again.';
        this.smart_import_data.is_scanning = false;
      }
    },

    subscribeToEvents() {
      if (window.Echo && this.client && this.client.id) {
        window.Echo.private('client.' + this.client.id)
          .listen('PlaybookPromptsGenerated', (e) => {
            if (this.playbook.id && e.playbook_id === this.playbook.id) {
              this.handlePromptsGenerated(e.content_key);
            }
          })
          .listen('ClientImageProcessed', (e) => {
            this.handleClientImageProcessed(e);
          });
      }
    },

    handlePromptsGenerated(contentKey) {
      // Fetch content from Redis using the API endpoint
      window.axios.get(`/api/playbooks/${this.client.url}/content/${contentKey}`)
        .then(response => {
          if (response.data.content) {
            const content = response.data.content;
            
            // Check if this is an error response
            if (content.error) {
              // Clear generating state
              this.generating_prompts = [];
              
              // Show error notification
              this.showNotification({
                icon: '/img/icons/avid-icons/danger.svg',
                text: 'Something went wrong when generating your Strategy Brief. Please try again.'
              });
              return;
            }
            
            // Update prompts with new content for keys that were generating
            this.generating_prompts.forEach(key => {
              if (content[key]) {
                // Store original content if not already stored
                if (!this.original_prompts[key]) {
                  this.original_prompts[key] = this.playbook.prompts[key];
                }
                
                // Store original citations if not already stored
                const citationKey = key + '_citations';
                if (!this.original_prompts[citationKey]) {
                  this.original_prompts[citationKey] = this.playbook.prompts[citationKey];
                }
                
                // Update with new content
                this.playbook.prompts[key] = content[key];
                this.local_prompts[key] = content[key];
                
                // Update with new citations if they exist
                if (content[citationKey]) {
                  this.playbook.prompts[citationKey] = content[citationKey];
                }
                
                // Mark as generated
                this.if_generated[key] = true;
              }
            });
            
            // Remove empty prompts from local_prompts after smart import
            const updatedLocalPrompts = {};
            for (let key in this.local_prompts) {
              const value = this.local_prompts[key];
              // Keep the prompt if it has content (not empty, not just whitespace)
              // Make sure we only check string values
              if (value && typeof value === 'string' && value.trim().length > 0) {
                updatedLocalPrompts[key] = value;
              }
            }
            this.local_prompts = updatedLocalPrompts;
            
            // Emit event to parent that strategy has changed
            this.$emit('strategy-changed');
            
            // Clear generating state
            this.generating_prompts = [];
          }
        })
        .catch(error => {
          console.error('Failed to fetch prompts content:', error);
          
          // Clear generating state on error
          this.generating_prompts = [];
          
          // Show error notification
          this.showNotification({
            icon: '/img/icons/avid-icons/danger.svg',
            text: 'Failed to fetch generated content'
          });
        });
    },

    revertPrompt(key) {
      if (this.original_prompts[key]) {
        // Swap new content with original content
        this.playbook.prompts[key] = this.original_prompts[key];
        this.local_prompts[key] = this.original_prompts[key];
        this.original_prompts[key] = null;
        
        // Also revert citations if they were stored
        const citationKey = key + '_citations';
        if (this.original_prompts[citationKey]) {
          this.playbook.prompts[citationKey] = this.original_prompts[citationKey];
          this.original_prompts[citationKey] = null;
        }
        
        // Remove from generated state
        delete this.if_generated[key];
      }
    },

    regeneratePrompt(key) {
      // Check if this prompt is currently generating
      if (this.isGenerating(key)) {
        // If already generating, just add to generating state and use existing response
        if (!this.generating_prompts.includes(key)) {
          this.generating_prompts.push(key);
        }
        return;
      }
      
      // Check if this is a regenerate (existing content) or generate (new content)
      const hasExistingContent = this.local_prompts[key] && this.local_prompts[key].trim().length > 0;
      
      if (hasExistingContent) {
        // Show feedback modal for regeneration
        this.showRegenerateFeedbackModal(key);
      } else {
        // Direct generation for new content
        this.startPromptGeneration(key);
      }
    },

    startPromptGeneration(key, feedbackText = '') {
      // Add this prompt to generating state
      if (!this.generating_prompts.includes(key)) {
        this.generating_prompts.push(key);
      }
      
      // Store original content if not already stored
      if (!this.original_prompts[key]) {
        this.original_prompts[key] = this.playbook.prompts[key];
      }
      
      // Track copy regeneration from strategy page
      if (statsigService.getClient() && this.generating_prompts.length == 1) {
        statsigService.logEvent('playbook_copy_regenerated', null, {
          playbook_id: this.playbook?.id,
          playbook_type_name: this.playbook?.playbook_type?.name,
          source: 'strategy_page'
        });
      }
      
      //Only start the generation if it is the first time it was clicked
      if(this.generating_prompts.length == 1) {
        // Make API call to regenerate all prompts (but only update the ones we're tracking)
        const payload = feedbackText ? { feedback: feedbackText } : {};
        
        window.axios.post(`/api/playbooks/${this.client.url}/generate_prompt/${this.playbook.url}`, payload)
          .catch(error => {
            console.error('Error starting regeneration:', error);
            // Remove from generating state on error
            const index = this.generating_prompts.indexOf(key);
            if (index > -1) {
              this.generating_prompts.splice(index, 1);
            }
          });
      }
    },

    isGenerating(index = null) {
      if(index) {
        return this.generating_prompts.includes(index);
      }
      return this.generating_prompts.length > 0;
    },

    startAddingCustomInput() {
      if (!this.is_adding_custom_input) {
        this.is_adding_custom_input = true;
        this.editing_mode = 'title';
        
        // Focus on the input after the DOM updates
        this.$nextTick(() => {
          if (this.$refs.customTitleInput) {
            this.$refs.customTitleInput.focus();
          }
        });
      }
    },

    startEditingCustomContent() {
      this.is_adding_custom_input = true;
      this.editing_mode = 'content';
      
      // Focus on the textarea after the DOM updates
      this.$nextTick(() => {
        if (this.$refs.customAnswerInput) {
          this.$refs.customAnswerInput.focus();
        }
      });
    },

    async saveCustomInput() {
      this.is_adding_custom_input = false;
      this.editing_mode = null;
      
      // Emit event to parent that strategy has changed
      this.$emit('strategy-changed');
    },
    getTextareaStyle(key) {
      const h = this.edit_area_heights[key];
      if (!h) return {};
      // Lock the textarea height to the measured content height
      return {
        height: h + 'px',
        minHeight: h + 'px',
        maxHeight: h + 'px',
        flex: '0 0 auto'
      };
    },

    async uploadFileToPlaybook(fileData) {
      try {
        // Set all cards to generating state immediately
        this.generating_prompts = Object.keys(this.playbook.prompts);
        
        // Call the new endpoint
        const response = await window.axios.post(
          `/api/playbooks/${this.client.url}/upload_file/${this.playbook.url}`,
          fileData
        );

        if (response.status === 201 && response.data.success) {
          // Close the modal
          this.closeSmartImportModal();
          
          // Show success notification
          this.showNotification({
            icon: '/img/icons/nav-icons/Playbooks.svg',
            text: 'File uploaded successfully! Prompts are regenerating with the new content.'
          });
        } else {
          throw new Error(response.data.message || 'Upload failed');
        }
      } catch (error) {
        console.error('Error uploading file to playbook:', error);
        
        // Clear generating state on error
        this.generating_prompts = [];
        
        // Show error message
        this.smart_import_data.error_message = error.response?.data?.message || 'Failed to upload file. Please try again.';
        
        // Reset scanning state
        this.smart_import_data.is_scanning = false;
        
        // Show error notification
        this.showNotification({
          icon: '/img/icons/avid-icons/danger.svg',
          text: 'Failed to upload file'
        });
        
        throw error; // Re-throw to be caught by scanDocument
      }
    },

    async uploadUrlToPlaybook(url) {
      try {
        // Set all cards to generating state immediately
        this.generating_prompts = Object.keys(this.playbook.prompts);
                
        // Call the new endpoint with URL
        const response = await window.axios.post(
          `/api/playbooks/${this.client.url}/upload_file/${this.playbook.url}`,
          {
            document_url: url
          }
        );

        if (response.status === 201 && response.data.success) {
          // console.log('URL processed and vector created successfully:', response.data);
          
          // Close the modal
          this.closeSmartImportModal();
          
          // Show success notification
          this.showNotification({
            icon: '/img/icons/nav-icons/Playbooks.svg',
            text: 'Document processed successfully! Prompts are regenerating with the new content.'
          });
        } else {
          throw new Error(response.data.message || 'URL processing failed');
        }
      } catch (error) {
        console.error(error);
        console.error(error.response?.data?.message);
        // Clear generating state on error
        this.generating_prompts = [];
        
        // Show error message
        this.smart_import_data.error_message = error.response?.data?.message || 'Failed to process URL. Please try again.';
        
        // Reset scanning state
        this.smart_import_data.is_scanning = false;
        
        // Show error notification
        this.showNotification({
          icon: '/img/icons/avid-icons/danger.svg',
          text: 'Failed to process URL'
        });
        
        throw error; // Re-throw to be caught by scanDocument
      }
    },

    showRegenerateFeedbackModal(key) {
      this.regenerate_feedback_data.prompt_key = key;
      this.regenerate_feedback_data.feedback_text = '';
      this.regenerate_feedback_data.error_message = '';
      
      const modalElement = document.getElementById('regenerateFeedbackModal');
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    },

    closeRegenerateFeedbackModal() {
      const modalElement = document.getElementById('regenerateFeedbackModal');
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    },

    resetRegenerateFeedbackForm() {
      this.regenerate_feedback_data.prompt_key = '';
      this.regenerate_feedback_data.feedback_text = '';
      this.regenerate_feedback_data.error_message = '';
    },

    submitRegenerateFeedback() {
      // Validate feedback text
      if (!this.regenerate_feedback_data.feedback_text.trim()) {
        this.regenerate_feedback_data.error_message = 'Please describe how you would like the strategy to be changed.';
        return;
      }

      // Clear any previous error
      this.regenerate_feedback_data.error_message = '';

      // Close the modal
      this.closeRegenerateFeedbackModal();
      // Start generation with feedback
      this.startPromptGeneration(this.regenerate_feedback_data.prompt_key, this.regenerate_feedback_data.feedback_text);

      this.resetRegenerateFeedbackForm();
    },
    
    getPlaybookImages() {
      // Return pending images (includes new uploads)
      return this.pending_images || [];
    },
    
    getValidImages() {
      // Filter out null/empty values
      return (this.pending_images || []).filter(img => img !== null && img !== '' && img !== undefined);
    },
    
    getImageUrl(image) {
      // Return public_url if available, otherwise gcs_path or fallback
      if (typeof image === 'string') {
        return image;
      }
      return image?.public_url || image?.gcs_path || image?.url || '';
    },
    
    openImageUploadModal() {
      this.image_modal_library.loading = false;
      this.image_modal_library.images = [];
      this.image_modal_library.slideIndex = 0;
      this.image_modal_library.total = 0;
      this.image_modal_library.selectedIds = [];
      this.image_modal_upload.pendingUrls = [];
      this.image_modal_upload.currentUrl = '';
      this.image_modal_upload.uploadProgress = null;
      this.image_modal_upload.dropzoneKey += 1;
      this.loadImageModalLibrary();
      this.$nextTick(() => {
        const modalElement = document.getElementById('imageUploadModal');
        if (modalElement) {
          const existingModal = window.bootstrap.Modal.getInstance(modalElement);
          if (existingModal) {
            existingModal.dispose();
          }
          const onShown = () => {
            this.$nextTick(() => {
              setTimeout(() => this.measureCarouselWrapper(), 50);
            });
            modalElement.removeEventListener('shown.bs.modal', onShown);
          };
          const onHidden = () => {
            if (this._carouselResizeObserver && this.$refs.imageModalCarouselWrapper) {
              this._carouselResizeObserver.disconnect();
            }
            modalElement.removeEventListener('hidden.bs.modal', onHidden);
          };
          modalElement.addEventListener('shown.bs.modal', onShown);
          modalElement.addEventListener('hidden.bs.modal', onHidden);
          const modal = new window.bootstrap.Modal(modalElement, {
            backdrop: 'static',
            keyboard: false,
            focus: true
          });
          modal.show();
        }
      });
    },
    measureCarouselWrapper() {
      const el = this.$refs.imageModalCarouselWrapper;
      if (el) {
        this.image_modal_library.wrapperWidth = el.offsetWidth;
        if (!this._carouselResizeObserver) {
          this._carouselResizeObserver = new ResizeObserver(() => {
            if (this.$refs.imageModalCarouselWrapper) {
              this.image_modal_library.wrapperWidth = this.$refs.imageModalCarouselWrapper.offsetWidth;
            }
          });
        }
        this._carouselResizeObserver.disconnect();
        this._carouselResizeObserver.observe(el);
      }
    },
    async loadImageModalLibrary() {
      if (!this.client?.url) return;
      this.image_modal_library.loading = true;
      try {
        const params = new URLSearchParams({
          per_page: 12,
          page: 1
        });
        const response = await window.axios.get(`/api/client/${this.client.url}/images?${params}`);
        const paginated = response.data;
        this.image_modal_library.images = paginated.data || [];
        this.image_modal_library.total = paginated.total ?? 0;
        this.image_modal_library.slideIndex = 0;
      } catch (error) {
        console.error('Error loading image library:', error);
        this.image_modal_library.images = [];
      } finally {
        this.image_modal_library.loading = false;
      }
    },
    async loadImageModalLibraryMore() {
      const lib = this.image_modal_library;
      if (!this.client?.url || lib.images.length >= lib.total) return;
      const perPage = 6;
      const page = Math.floor(lib.images.length / perPage) + 1;
      this.image_modal_library.loading = true;
      try {
        const params = new URLSearchParams({
          per_page: perPage,
          page: String(page)
        });
        const response = await window.axios.get(`/api/client/${this.client.url}/images?${params}`);
        const paginated = response.data;
        const newImages = paginated.data || [];
        this.image_modal_library.images = this.image_modal_library.images.concat(newImages);
      } catch (error) {
        console.error('Error loading more library images:', error);
      } finally {
        this.image_modal_library.loading = false;
      }
    },
    imageModalLibraryPrev() {
      if (this.image_modal_library.slideIndex <= 0) return;
      this.image_modal_library.slideIndex -= 1;
    },
    async imageModalLibraryNext() {
      const lib = this.image_modal_library;
      const displayImages = this.image_modal_library_display_images;
      const nextWindowStart = (lib.slideIndex + 1) * lib.visibleCount;
      if (nextWindowStart >= displayImages.length && lib.images.length < lib.total) {
        await this.loadImageModalLibraryMore();
        await this.$nextTick();
      }
      if ((lib.slideIndex + 1) * lib.visibleCount <= displayImages.length) {
        this.image_modal_library.slideIndex += 1;
      }
    },
    toggleLibraryImageSelection(image) {
      const idx = this.image_modal_library.selectedIds.indexOf(image.id);
      if (idx > -1) {
        this.image_modal_library.selectedIds.splice(idx, 1);
      } else {
        this.image_modal_library.selectedIds.push(image.id);
      }
    },
    isLibraryImageSelected(imageId) {
      return this.image_modal_library.selectedIds.includes(imageId);
    },
    getLibraryImageUrl(image) {
      if (image.public_url) return image.public_url;
      if (image.gcs_path) return image.gcs_path.startsWith('http') ? image.gcs_path : 'https://app.avidai.com/' + image.gcs_path;
      return image.url || '';
    },
    handleModalUploadImageUpdate(value) {
      if (value && value !== '') {
        const url = this.normalizeImageUrl(value);
        this.image_modal_upload.pendingUrls.push({ url, key: 'upload_' + Date.now() + '_' + Math.random().toString(36).slice(2) });
        this.image_modal_upload.currentUrl = '';
        if (!this.image_modal_upload.uploadProgress) {
          this.image_modal_upload.dropzoneKey += 1;
        }
      }
    },
    handleModalUploadProgress(payload) {
      this.image_modal_upload.uploadProgress = payload;
      if (payload === null) {
        this.image_modal_upload.dropzoneKey += 1;
      }
    },
    removePendingUploadUrl(index) {
      this.image_modal_upload.pendingUrls.splice(index, 1);
    },
    normalizeImageUrl(value) {
      if (!value) return '';
      let url = String(value).replace('%2F', '/');
      if (!url.startsWith('http')) url = 'https:' + url;
      return url;
    },
    confirmAddImagesFromModal() {
      if (!this.canAddImagesFromModal) return;
      const hasLibrary = this.image_modal_library.selectedIds.length > 0;
      const hasUploads = this.image_modal_upload.pendingUrls.length > 0;
      if (hasLibrary && !hasUploads) {
        const selected = this.image_modal_library_display_images.filter(img => this.image_modal_library.selectedIds.includes(img.id));
        const toAdd = selected.map(img => ({
          ...img,
          public_url: this.getLibraryImageUrl(img)
        }));
        toAdd.forEach(img => this.pending_images.push(img));
      } else if (hasUploads && !hasLibrary) {
        this.image_modal_upload.pendingUrls.forEach(item => {
          const tempImage = {
            id: 'temp_' + Date.now() + '_' + Math.random().toString(36).slice(2),
            public_url: this.normalizeImageUrl(item.url),
            gcs_path: null,
            image_classification: null,
            name: 'New Image',
            campaign_specific: true,
            is_pending: true
          };
          this.pending_images.push(tempImage);
        });
      }
      const modalElement = document.getElementById('imageUploadModal');
      if (modalElement) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }
      this.image_modal_library.selectedIds = [];
      this.image_modal_upload.pendingUrls = [];
      this.image_modal_upload.currentUrl = '';
      this.image_modal_upload.dropzoneKey += 1;
      this.$emit('strategy-changed');
    },
    
    removeImage(image_index) {
      const validImages = this.getValidImages();
      if (validImages.length > 0 && image_index < validImages.length) {
        const image = validImages[image_index];
        
        // If it's a saved image (has real ID), mark for deletion
        if (image.id && !image.id.toString().startsWith('temp_')) {
          this.pending_image_deletes.push(image.id);
        }
        
        // Remove from pending images
        const index = this.pending_images.findIndex(img => {
          if (typeof img === 'string') {
            return img === image;
          }
          return img.id === image.id || img.public_url === image.public_url;
        });
        if (index > -1) {
          this.pending_images.splice(index, 1);
        }
        
        // Emit event that strategy has changed
        this.$emit('strategy-changed');
      }
    },
    
    openImagePreview(image_index) {
      const validImages = this.getValidImages();
      if (validImages.length > 0 && image_index < validImages.length) {
        const image = validImages[image_index];
        this.previewImageData = {
          url: this.getImageUrl(image),
          name: image?.name || 'Image',
          image_classification: image?.image_classification || ''
        };
        this.previewImageIndex = image_index;
        
        this.$nextTick(() => {
          const modalElement = document.getElementById('imagePreviewModal');
          if (modalElement) {
            const existingModal = window.bootstrap.Modal.getInstance(modalElement);
            if (existingModal) {
              existingModal.dispose();
            }
            const modal = new window.bootstrap.Modal(modalElement, {
              backdrop: 'static',
              keyboard: false,
              focus: true
            });
            modal.show();
          }
        });
      }
    },
    
    closeImagePreviewModal() {
      this.previewImageData = {
        url: '',
        name: '',
        image_classification: ''
      };
      this.previewImageIndex = null;
      
      const modalElement = document.getElementById('imagePreviewModal');
      if (modalElement) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) {
          modal.hide();
        }
      }
    },
    
    handleDragOver(event) {
      event.preventDefault();
      event.currentTarget.style.borderColor = '#0d6efd';
    },
    
    handleDrop(event) {
      event.preventDefault();
      event.currentTarget.style.borderColor = '#ccc';
      this.openImageUploadModal();
    },
    
    getClassificationBadgeClass(classification) {
      const classes = {
        'mission_in_action': 'bg-primary',
        'location': 'bg-info',
        'products': 'bg-warning',
        'logo': 'bg-success',
        'design_styles': 'bg-secondary',
        'other': 'bg-secondary',
      };
      return classes[classification] || 'bg-secondary';
    },
    
    formatClassification(classification) {
      if (!classification) return '';
      return classification.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    },
    
    hasOnlyImagesChanged() {
      // Filter initial prompts snapshot to match what's shown on screen (exclude custom inputs and citations)
      const filteredInitialPrompts = JSON.parse(JSON.stringify(this.initial_prompts_snapshot));
      if(this.hasCustomInput()) {
        let placeholder;
        for(let key in this.initial_prompts_snapshot) {
          if(this.isCustomInput(key)) {
            placeholder = key;
            break;
          }
        }
        if(placeholder) {
          delete filteredInitialPrompts[placeholder];
        }
      }
      // Remove citation keys
      for(let key in filteredInitialPrompts) {
        if(key.endsWith('_citations')) {
          delete filteredInitialPrompts[key];
        }
      }
      
      // Check if prompts have changed (compare only what's shown on screen)
      const currentPrompts = JSON.stringify(this.local_prompts);
      const initialPrompts = JSON.stringify(filteredInitialPrompts);
      const promptsChanged = currentPrompts !== initialPrompts;
      
      // Check if custom input changed
      const customInputKey = this.getCustomInputKey();
      const currentCustomInput = this.custom_input_answer || '';
      const initialCustomInput = this.initial_prompts_snapshot?.[customInputKey] || '';
      const customInputChanged = currentCustomInput !== initialCustomInput;
      
      // Check if images changed
      // Get current image IDs (excluding temp IDs)
      const currentImageIds = this.getValidImages()
        .filter(img => {
          if (typeof img === 'string') return false;
          return img.id && !img.id.toString().startsWith('temp_');
        })
        .map(img => img.id)
        .sort();
      
      // Get initial image IDs
      const initialImageIds = (this.initial_images_snapshot || [])
        .filter(img => img && img.id)
        .map(img => img.id)
        .sort();
      
      // Check if there are pending uploads or deletes
      const hasPendingUploads = this.pending_images.some(img => {
        if (typeof img === 'string') return false;
        return img.is_pending || (img.id && img.id.toString().startsWith('temp_'));
      });
      const hasPendingDeletes = this.pending_image_deletes.length > 0;
      
      // Images changed if IDs are different, or there are pending uploads/deletes
      const imagesChanged = JSON.stringify(currentImageIds) !== JSON.stringify(initialImageIds) || 
                           hasPendingUploads ||
                           hasPendingDeletes;
      
      // Only images changed if prompts and custom input unchanged, but images changed
      return !promptsChanged && !customInputChanged && imagesChanged;
    },
    
    getCustomInputKey() {
      if (!this.custom_input_title) return null;
      return this.custom_input_title.toLowerCase()
        .replace(/[^a-z\s]/g, '')
        .replace(/ /g, '_');
    },
    
    handleClientImageProcessed(eventData) {
      const processedImage = eventData.image;
      const playbookIds = eventData.playbook_ids || [];

      // Check if this image belongs to the current playbook
      const belongsToCurrentPlaybook = this.playbook?.id && playbookIds.includes(this.playbook.id);

      // Find the image in pending_images by ID and update it with the processed data
      const imageIndex = this.pending_images.findIndex(img => {
        if (typeof img === 'string') return false;
        // Match by ID
        return img.id && img.id.toString() === processedImage.id.toString();
      });
      
      if (imageIndex > -1) {
        // Preserve the existing public_url if the processed image doesn't have one yet
        const existingImage = this.pending_images[imageIndex];
        const processedPublicUrl = processedImage.public_url || 
                                   (processedImage.gcs_path ? 'https://app.avidai.com/' + processedImage.gcs_path : null);
        
        // Update the image with processed data
        this.pending_images[imageIndex] = {
          ...processedImage,
          public_url: processedPublicUrl || existingImage.public_url, // Keep existing URL if processed one is missing
          is_pending: false // Mark as no longer pending
        };
        
        // Also update in playbookImages if it exists there
        const playbookImageIndex = this.playbookImages.findIndex(img => img.id === processedImage.id);
        if (playbookImageIndex > -1) {
          const existingPlaybookImage = this.playbookImages[playbookImageIndex];
          this.playbookImages[playbookImageIndex] = {
            ...processedImage,
            public_url: processedPublicUrl || existingPlaybookImage.public_url
          };
        }
        
        // Force update
        this.$forceUpdate();
      } else if (belongsToCurrentPlaybook) {
        // Image belongs to this playbook but isn't in pending_images yet (e.g., from Google Doc upload)
        // Add it to pending_images
        const processedPublicUrl = processedImage.public_url ||
          (processedImage.gcs_path ? 'https://app.avidai.com/' + processedImage.gcs_path : null);

        const imageToAdd = {
          ...processedImage,
          public_url: processedPublicUrl,
          is_pending: false
        };

        this.pending_images.push(imageToAdd);
        this.playbookImages.push(imageToAdd);

        // Update initial snapshot to include this image
        this.initial_images_snapshot.push(imageToAdd);

        // Force update
        this.$forceUpdate();
      }
    }
  }
}
</script>

<style scoped>
.image-modal-carousel-wrapper {
  flex: 1 1 0;
}
.image-modal-carousel-track {
  transition: transform 0.3s ease-out;
}
.image-modal-library-card {
  flex: 0 0 calc((100% - (5 * 8px)) / 6);
  aspect-ratio: 1 / 1;
  height: auto;
  cursor: pointer;
  position: relative;
}
.image-modal-library-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.image-modal-library-card-selected {
  border: 2px solid #0d6efd !important;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
}
.image-modal-library-overlay {
  position: absolute;
  inset: 0;
  background: rgba(13, 110, 253, 0.35);
  pointer-events: none;
}
.image-modal-upload-thumb {
  width: 80px;
  height: 80px;
}
.image-modal-upload-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>